import { axisBottom, axisRight } from 'd3-axis';
import { scaleLinear, scalePoint } from 'd3-scale';
import 'd3-transition';

import { createSvg } from '../helpers';
import { ChartData, SvgParam } from '../types';

import {
  AxisesProps,
  CalcProps,
  ChartVariants,
  DrawProps,
  RectangleProps,
  WidthRatioProps
} from './types';

export const isChartHorizontal = (variant: ChartVariants) =>
  variant === 'horizontal';

const createHorizontalChartRectangles = ({
  svg,
  data,
  height,
  margin,
  width,
  maxValue,
  barsHorizontalPadding
}: RectangleProps & SvgParam & CalcProps) => {
  const xScale = scaleLinear()
    .domain([0, data.length])
    .range([0, width - margin.right - 2 * barsHorizontalPadding]);
  const yScale = scaleLinear()
    .domain([0, maxValue])
    .range([height, 0]);

  const calcXAxis = (_: ChartData, i: number) =>
    xScale(i) + barsHorizontalPadding;

  const rects = svg
    .selectAll('rect')
    .data(data)
    .enter()
    .append('rect')
    .attr('class', 'bar')
    .attr('height', 0)
    .attr('y', height)
    .attr('x', calcXAxis)
    .attr('width', xScale(1) + 0.3);

  rects
    .transition()
    .duration(400)
    .attr('y', ({ value }: ChartData) => yScale(value))
    .attr('height', ({ value }: ChartData) => height - yScale(value))
    .delay((_: ChartData, i: number) => i * 30)
    .attr('fill', 'rgba(0,193,226,0.6)');

  return rects;
};

const getWidthRatio = ({
  width,
  margin,
  maxValue,
  chartWidthRatio
}: WidthRatioProps & CalcProps) => {
  const maxAllowedWidth = (width - margin.right) * chartWidthRatio;

  return maxAllowedWidth / maxValue;
};

const createVerticalChartRectangles = ({
  svg,
  data,
  width,
  height,
  margin,
  maxValue,
  rectWidth,
  barMargin,
  rectHeight,
  chartWidthRatio,
  barsVerticalPadding
}: RectangleProps & SvgParam & CalcProps) => {
  const widthRatio = getWidthRatio({
    width,
    margin,
    maxValue,
    rectWidth,
    rectHeight,
    chartWidthRatio
  });

  const calcYAxis = (_: ChartData, index: number) => {
    const gap = index ? barMargin : 0;
    return (
      height -
      (index + 1) * rectHeight -
      barsVerticalPadding -
      gap -
      margin.bottom
    );
  };

  const rects = svg
    .selectAll('rect')
    .data(data)
    .enter()
    .append('rect')
    .attr('class', 'bar')
    .attr('x', 0)
    .attr('y', calcYAxis)
    .attr('width', 0)
    .attr('height', rectHeight)
    .style('margin-top', barMargin);

  rects
    .transition()
    .duration(1400)
    .attr('width', ({ value }: ChartData) => value * widthRatio)
    .delay((_: ChartData, i: number) => i * 30)
    .attr('fill', 'rgba(0,193,226,0.6)');

  return rects;
};

const createHorizontalChartAxises = ({
  svg,
  width,
  height,
  maxValue,
  margin
}: AxisesProps & SvgParam & { width: number }) => {
  const innerWidth = width - margin.right - margin.left;

  const xScale = scaleLinear()
    .domain([0, 1])
    .range([0, innerWidth]);
  const yScale = scaleLinear()
    .domain([0, maxValue])
    .range([height - margin.bottom - margin.top, 0]);
  const xAxisGrid = axisBottom(xScale)
    .tickSize(-height)
    .ticks(10);
  const yAxisGrid = axisRight(yScale)
    .tickSize(innerWidth)
    .tickFormat((x: number) =>
      !x ? '' : x > 1000 ? `${(x / 1000).toFixed(1)}k` : `${x}`
    )
    .ticks(5);
  svg
    .append('g')
    .attr('class', 'x axis-grid')
    .style('transform', `translate(0, ${height}px)`)
    .call(xAxisGrid);
  svg
    .append('g')
    .attr('class', 'y axis-grid')
    .call(yAxisGrid);
};

const createVerticalChartAxises = ({
  svg,
  width,
  height,
  maxValue,
  margin
}: AxisesProps & SvgParam & { width: number }) => {
  const innerWidth = width - margin.right - margin.left;

  const x = scalePoint()
    .domain(['0', '20', '40', '60', '80', '100', ''])
    .range([0, innerWidth]);
  const yScale = scaleLinear()
    .domain([0, maxValue])
    .range([height - margin.bottom - margin.top, 0]);
  const xAxisGrid = axisBottom(x)
    .tickSize(height - margin.bottom)
    .tickFormat((v: string) => (v && +v ? `${v}%` : ''));
  const yAxisGrid = axisRight(yScale)
    .tickSize(innerWidth)
    .tickFormat((x: number) => (x ? `${x}` : ''))
    .ticks(0);
  svg
    .append('g')
    .attr('class', 'x axis-grid')
    .call(xAxisGrid);
  svg
    .append('g')
    .attr('class', 'y axis-grid')
    .call(yAxisGrid);
};

const getCreateRectangles = (variant: ChartVariants) =>
  isChartHorizontal(variant)
    ? createHorizontalChartRectangles
    : createVerticalChartRectangles;

const getCreateAxises = (variant: ChartVariants) =>
  isChartHorizontal(variant)
    ? createHorizontalChartAxises
    : createVerticalChartAxises;

export const drawSvg = ({
  containerRef,
  drawTooltipOrLabels,
  ...props
}: DrawProps) => {
  const {
    data,
    height,
    margin,
    barMargin,
    barsVerticalPadding,
    barsHorizontalPadding
  } = props;
  const width = containerRef?.current?.clientWidth!;
  const innerWidth = width - props.margin.right - props.margin.left;
  const rectWidth = (innerWidth - barsHorizontalPadding * 2) / data.length;
  const rectHeight =
    (height -
      margin.top -
      margin.bottom -
      barsVerticalPadding * 2 -
      (data.length - 1) * barMargin) /
    data.length;

  const calcProps = { width, rectWidth, rectHeight };

  const isHorizontalChart = isChartHorizontal(props.variant);
  const svgClassName = isHorizontalChart
    ? 'horizontal-chart'
    : 'vertical-chart';

  const svg = createSvg({ ...props, ...calcProps, className: svgClassName });
  const createRectangles = getCreateRectangles(props.variant);

  const createAxises = getCreateAxises(props.variant);
  createAxises({ ...props, ...calcProps, svg });

  const rects = createRectangles({
    ...props,
    ...calcProps,
    svg
  });

  drawTooltipOrLabels &&
    drawTooltipOrLabels({ svg, rects, ...calcProps, ...props });
};
